import { useState, type ReactElement } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import type { ToolName } from '@arcanum-hq/shared-types';

export interface ToolErrorCalloutProps {
  tool: ToolName;
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

const MAX_VALID_INPUTS_VISIBLE = 12;

/**
 * Inline callout rendered in `ChatThread`'s AI message branch when an
 * `apply_market_defaults` / `run_sensitivity` / etc. tool returned a
 * structured `tool_error` SSE event. Subdued danger token, collapsed by
 * default. Click the chevron to expand and reveal the `details` payload —
 * `validInputs` chips, `acceptableRange`, `zodIssues` table, or
 * `iterations` + `lastError`.
 *
 * The agent reads RULE 7 from its system prompt and self-corrects on the
 * next turn — the callout is purely informational for the human reader.
 */
export function ToolErrorCallout({ tool, code, message, details }: ToolErrorCalloutProps) {
  const [expanded, setExpanded] = useState(false);

  const hasDetails = !!details && Object.keys(details).length > 0;

  return (
    <div className="bg-danger-dim border border-danger/15 rounded-[3px] px-3 py-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        disabled={!hasDetails}
        className={clsx(
          'w-full flex items-center gap-2 text-left',
          hasDetails ? 'cursor-pointer' : 'cursor-default',
        )}
        aria-expanded={hasDetails ? expanded : undefined}
        aria-label={hasDetails ? (expanded ? 'Collapse error details' : 'Expand error details') : undefined}
      >
        <ChevronRight
          size={12}
          className={clsx(
            'text-danger/70 shrink-0 transition-transform duration-150',
            hasDetails && expanded && 'rotate-90',
            !hasDetails && 'opacity-30',
          )}
          aria-hidden="true"
        />
        <span className="text-xs text-danger font-mono">{tool}</span>
        <span className="text-xs text-danger/70 font-mono">{code}</span>
        <span className="text-sm text-text font-body truncate min-w-0 flex-1">{message}</span>
      </button>

      {hasDetails && expanded && (
        <dl className="mt-2 pt-2 border-t border-danger/10 flex flex-col gap-1.5 text-xs font-mono">
          {renderDetails(details!)}
        </dl>
      )}
    </div>
  );
}

function renderDetails(details: Record<string, unknown>): ReactElement[] {
  const out: ReactElement[] = [];

  // inputName first (high-signal context for which arg the agent got wrong)
  if (typeof details.inputName === 'string') {
    out.push(
      <div key="inputName" className="flex gap-2">
        <dt className="text-text-dim shrink-0">inputName</dt>
        <dd className="text-text font-semibold">{details.inputName}</dd>
      </div>,
    );
  }

  if (Array.isArray(details.validInputs) && details.validInputs.every((v) => typeof v === 'string')) {
    const all = details.validInputs as string[];
    const visible = all.slice(0, MAX_VALID_INPUTS_VISIBLE);
    const overflow = all.length - visible.length;
    out.push(
      <div key="validInputs" className="flex flex-col gap-1">
        <dt className="text-text-dim">validInputs</dt>
        <dd className="flex flex-wrap gap-1">
          {visible.map((v) => (
            <span
              key={v}
              className="inline-block px-1.5 py-0.5 bg-panel-bg border border-panel-border rounded-[2px] text-text-muted tabular-nums"
            >
              {v}
            </span>
          ))}
          {overflow > 0 && (
            <span className="inline-block px-1.5 py-0.5 text-text-dim">and {overflow} more</span>
          )}
        </dd>
      </div>,
    );
  }

  if (
    details.acceptableRange &&
    typeof details.acceptableRange === 'object' &&
    typeof (details.acceptableRange as Record<string, unknown>).min === 'number' &&
    typeof (details.acceptableRange as Record<string, unknown>).max === 'number'
  ) {
    const range = details.acceptableRange as { min: number; max: number };
    out.push(
      <div key="acceptableRange" className="flex gap-2">
        <dt className="text-text-dim shrink-0">acceptableRange</dt>
        <dd className="text-text tabular-nums">
          min: {range.min} — max: {range.max}
        </dd>
      </div>,
    );
  }

  if (typeof details.iterations === 'number' || typeof details.lastError === 'number') {
    const parts: string[] = [];
    if (typeof details.iterations === 'number') parts.push(`iterations: ${details.iterations}`);
    if (typeof details.lastError === 'number') parts.push(`lastError: ${details.lastError}`);
    out.push(
      <div key="iterLast" className="flex gap-2">
        <dt className="text-text-dim shrink-0">convergence</dt>
        <dd className="text-text tabular-nums">{parts.join(', ')}</dd>
      </div>,
    );
  }

  if (
    Array.isArray(details.zodIssues) &&
    details.zodIssues.every(
      (i) =>
        i &&
        typeof i === 'object' &&
        typeof (i as Record<string, unknown>).path === 'string' &&
        typeof (i as Record<string, unknown>).message === 'string',
    )
  ) {
    const issues = details.zodIssues as Array<{ path: string; message: string }>;
    out.push(
      <div key="zodIssues" className="flex flex-col gap-0.5">
        <dt className="text-text-dim">zodIssues</dt>
        <dd>
          <table className="border-collapse">
            <tbody>
              {issues.map((issue, i) => (
                <tr key={i}>
                  <td className="text-text pr-3 align-top">{issue.path}</td>
                  <td className="text-text-muted align-top">{issue.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </dd>
      </div>,
    );
  }

  // Catch-all for unknown keys — render as JSON for debug visibility
  const knownKeys = new Set(['inputName', 'validInputs', 'acceptableRange', 'iterations', 'lastError', 'zodIssues']);
  const unknownEntries = Object.entries(details).filter(([k]) => !knownKeys.has(k));
  if (unknownEntries.length > 0) {
    out.push(
      <div key="unknown" className="flex flex-col gap-0.5">
        <dt className="text-text-dim">other</dt>
        <dd className="text-text-dim text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(Object.fromEntries(unknownEntries), null, 2)}
        </dd>
      </div>,
    );
  }

  return out;
}
